import { action, computed, observable } from 'mobx';
import { RootStore } from 'stores/rootStore';
import { AccountData, AccountDetailData, AccountSummaryData, PendingChanges } from './model';
import { ConfirmType, DetailPage, Flow, Flows, flows } from 'pages/Accounts/Detail/enums';
import { DocumentData } from './documentsStore';
import history from 'helpers/routerHistory';
import {
  AccountStatus,
  AccountMode,
  ActiveStatus,
  ClientView,
  Currency,
  Method2FA,
  Request,
  TransferOption,
} from 'enums';
import { ApiStore } from 'stores/apiStore';
import { SingleValueResponse } from 'common/interfaces';
import {
  API_ACCOUNT_DETAIL_CHANGE,
  API_ACCOUNT_DETAIL_PLAN,
  API_ACCOUNT_DETAIL_PLAN_HISTORY,
  API_ACCOUNT_DETAIL_SUMMARY,
  API_ACCOUNT_DETAIL_SUMMARY_ACCOUNT,
  API_ACCOUNT_DETAIL_CHECK_SIGNING,
  API_ACCOUNT_DETAIL_SEND_PORTAL,
  API_ACCOUNT_DETAIL_REQUEST,
  API_ACCOUNT_DETAIL_REQUEST_AMDMNT,
  API_ACCOUNT_DETAIL_SET_2FA,
  API_ACCOUNT_DETAIL_TERMINATE,
  API_ACCOUNT_DETAIL_SET_2FA_METHOD,
  API_ACCOUNT_DETAIL_EXT_PRODUCTS_REFRESH,
} from 'api/endpoints';
import { ACCOUNTS_DETAIL, ACCOUNTS_EDIT, ACCOUNTS_DOCUMENTS } from 'routes';
import { AccountBasic } from 'stores/workflowStores/workflowBasicStore/model';

export class AccountDetailStore {
  rootStore: RootStore;
  apiStore: ApiStore;
  accountId: string;
  tabChange: Function;
  titleChange: Function;
  firstEdit: boolean;
  editTab: DetailPage | undefined; // tab to be open after confirmation
  noDetailReset: boolean | undefined; // cancel with no section switch, after confirmation
  userMode: boolean;
  signOnly: boolean;

  flowStepIndex: number | null = null;
  editFlow: Flow | undefined; // section to be edited after confirmation

  @observable currentPage?: DetailPage;
  @observable flowStartPage?: DetailPage;
  @observable status: AccountStatus | undefined;
  @observable mode: AccountMode | undefined;
  @observable activeStatus: ActiveStatus | undefined;
  @observable canTerminateContract = false;
  @observable locked = false;
  @observable riskProfileMismatch = false;
  @observable data: AccountData;
  @observable dataProcessed = false;
  @observable currency: Currency;
  @observable basicData?: AccountBasic;
  @observable editMode?: boolean;
  @observable newEdit?: boolean;
  @observable transferOption = TransferOption.PENDING;
  @observable pendingChanges: PendingChanges;
  @observable pendingSignature: boolean;
  @observable pendingRequests: Request[];
  @observable pendingWood: boolean;
  @observable processing: boolean; // BE is generating documents
  @observable processingError: boolean; // document generation failed
  @observable types: string; // document generation template types

  @observable confirmDialog: boolean; // confirm editing, canceling, etc.
  @observable confirmRequest: boolean; // confirm request cancellation
  @observable infoDialog: boolean; // additional info, eg. cannot cancel changes
  @observable infoText?: string; // additional info, eg. cannot cancel changes

  @observable refreshTimestamp: string;

  @observable taxMode?: 'OBH' | 'PPP'; // EDS-4915

  @observable flow: Flow | null = null;

  constructor(
    rootStore: RootStore,
    accountId: string,
    tabChange: Function,
    titleChange: Function,
    userMode: boolean,
    signOnly: boolean,
  ) {
    this.rootStore = rootStore;
    this.apiStore = this.rootStore.stores.apiStore;
    this.accountId = accountId;
    this.data = { summary: null, detail: null };
    this.currency = Currency.NONE;

    this.status = signOnly ? AccountStatus.TO_BE_SIGNED : undefined;
    this.mode = undefined;
    this.editMode = undefined;
    this.pendingChanges = PendingChanges.NONE;
    this.pendingSignature = false;
    this.pendingRequests = [];
    this.pendingWood = false;
    this.processing = false;
    this.processingError = false;
    this.types = '';
    this.currentPage = undefined;
    this.tabChange = tabChange; // set tab in content page subsection
    this.titleChange = titleChange;
    this.firstEdit = true;
    this.editTab = undefined; //
    this.editFlow = undefined;

    //this.noDetailReset = undefined;

    this.confirmDialog = false;
    this.confirmRequest = false;
    this.infoDialog = false;
    this.infoText = undefined;

    this.userMode = userMode;
    this.signOnly = signOnly;

    this.refreshTimestamp = '';
  }

  @action
  setAccountId = (accountId: string) => (this.accountId = accountId);

  @action
  setFlow = (flow: Flow) => {
    this.flow = flow;
    this.setPage(this.flowStartPage || this.flow.defaultPage || this.flow.steps[0].id);
    this.currentPage && this.setFlowStep(this.currentPage);
    this.titleChange(`accountDetail_edit_${this.flow.id}`);
  };

  @computed
  get flowStep(): DetailPage | null {
    return this.flow && this.flowStepIndex !== null ? this.flow.steps[this.flowStepIndex].id : null;
  }

  get firstStep(): boolean {
    return !!this.flow && this.flowStepIndex === 0;
  }
  get lastStep(): boolean {
    return !!this.flow && this.flowStepIndex === this.flow.steps.length - 1;
  }

  get prevStepLabel(): string {
    return !this.firstStep && this.flowStepIndex !== null
      ? this.flow!.steps[this.flowStepIndex - 1].buttonTitle || this.flow!.steps[this.flowStepIndex - 1].title
      : '';
  }

  get nextStepLabel(): string {
    return !this.lastStep && this.flowStepIndex !== null
      ? this.flow!.steps[this.flowStepIndex + 1].buttonTitle || this.flow!.steps[this.flowStepIndex + 1].title
      : '';
  }

  @action
  setFlowStep = (page: DetailPage) => {
    const step = this.flow?.steps.findIndex((step) => step.id === page);
    if (this.flow && step !== -1 && step !== undefined) {
      this.flowStepIndex = step;
      this.setPage(this.flow.steps[step].id);
    }
  };

  @action
  goFwd = () => {
    if (!this.lastStep && !!this.flow && this.flowStepIndex !== null) {
      this.flowStepIndex++;
      this.setPage(this.flow.steps[this.flowStepIndex].id);
    }
  };

  @action
  goBack = () => {
    if (!this.firstStep && !!this.flow && this.flowStepIndex !== null) {
      this.flowStepIndex--;
      this.setPage(this.flow.steps[this.flowStepIndex].id);
    }
  };

  @action setPage = (page: DetailPage) => {
    this.currentPage = page;
    this.flowStartPage = undefined;
    this.tabChange(page);
  };

  @action setFlowStartPage = (page?: DetailPage) => {
    this.flowStartPage = page;
  };

  fetchData = async () => {
    await this.fetchBasicData();
    await this.fetchAccountDetail();
  };

  fetchBasicData = async () => {
    this.data.summary = null;
    this.dataProcessed = false;
    this.transferOption = TransferOption.PENDING;
    this.rootStore.stores.uiStore.setLoading(true);

    const response = await this.apiStore.get<AccountSummaryData>(API_ACCOUNT_DETAIL_SUMMARY(this.accountId));
    if (response?.data) {
      this.data.summary = response.data;

      //console.log('loaded');
      const currentAccount = this.data.summary?.accountsSummary.filter(
        (account) => account.accountId === this.accountId,
      )[0];

      if (currentAccount) {
        this.processing = currentAccount.processing;
        this.processingError = false;
        this.pendingSignature = currentAccount.pendingChangeSignature;
        this.pendingRequests = currentAccount.pendingRequestSignature;
        this.pendingChanges = currentAccount.pendingChanges;
        this.pendingWood = currentAccount.status === AccountStatus.AWAITING_APPROVAL;
        this.status = currentAccount.status;
        this.mode = currentAccount.mode;
        this.activeStatus = currentAccount.activeStatus;
        this.currency = currentAccount.currency;
        this.canTerminateContract = currentAccount.canTerminateContract;
        this.locked = currentAccount.activeStatus !== undefined;

        const transferTargetAccounts = this.data.summary.accountsSummary.filter(
          (a) =>
            a.accountId !== currentAccount.accountId &&
            (a.status === AccountStatus.ACTIVE || a.status === AccountStatus.AWAITING_APPROVAL) &&
            !a.activeStatus &&
            a.feePaidAll,
        );
        if (this.status !== AccountStatus.ACTIVE || this.activeStatus) {
          this.transferOption = TransferOption.DISABLED_STATE;
        } else if (this.mode === AccountMode.LTIP) {
          this.transferOption = TransferOption.DISABLED_LTIP;
        } else if (!transferTargetAccounts.length) {
          this.transferOption = TransferOption.DISABLED_NOTARGET;
        } else if (currentAccount.pendingTransfer.some((pt) => pt.target)) {
          this.transferOption = TransferOption.DISABLED_TRANSFER;
        } else if (currentAccount.pendingTransfer.length) {
          this.transferOption = TransferOption.DISABLED_PORTFOLIOS;
        } else if (currentAccount.pendingWithdrawal?.length) {
          this.transferOption = TransferOption.DISABLED_WITHDRAWAL;
        } else {
          this.transferOption = TransferOption.ENABLED;
        }

        this.basicData = {
          client: {
            firstname: this.data.summary.ownerData.firstName,
            retired: this.data.summary.ownerData.retired,
            personType: this.data.summary.ownerData.personType,
            taxResidency: this.data.summary.ownerData.taxResidency.country,
          },
          currency: this.currency,
        };

        // draft exists (docs not sent yet)
        if (this.pendingChanges !== PendingChanges.NONE && !this.pendingSignature && !this.userMode) {
          this.setEditMode(this.pendingChanges);

          switch (this.pendingChanges) {
            case PendingChanges.WITHDRAWAL:
              this.setFlow(this.status === AccountStatus.ACTIVE ? flows[Flows.WITHDRAWAL] : flows[Flows.CLOSE_ACCOUNT]);
              break;
            case PendingChanges.FEES:
              this.setFlow(flows[Flows.PRICELIST]);
              break;
            case PendingChanges.PERSONAL:
              this.setFlow(flows[Flows.PERSONAL]);
              break;
            case PendingChanges.TRANSFER:
              this.setFlow(flows[Flows.TRANSFER]);
              break;
            default:
              this.setFlow(flows[Flows.PLAN]);
              break;
          }
        } else {
          this.editMode = false;
          if (this.pendingSignature && !this.userMode) this.setPage(DetailPage.DASHBOARD); // docs sent
        }

        // get the document types if generating underway and page refreshed
        if (this.processing && this.types === '') {
          const response = await this.apiStore.get<DocumentData[]>(API_ACCOUNT_DETAIL_CHANGE(this.accountId));
          if (response?.data) {
            this.types = response.data.map((doc: DocumentData) => doc.type).join(',');
          }
        }
      }
      //console.log('processed');
      this.dataProcessed = true;
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action
  fetchAccountDetail = async () => {
    this.data.detail = null;
    const response = await this.apiStore.get<AccountDetailData>(API_ACCOUNT_DETAIL_SUMMARY_ACCOUNT(this.accountId));
    if (response?.data) {
      this.data.detail = response.data;
      this.taxMode = response.data.clientTaxMode;
      !this.userMode &&
        this.rootStore.stores.breadcrumbsStore.updateByTitle(this.accountId, this.data.detail.variableSymbol);
      this.riskProfileMismatch = !!this.data.detail.riskProfileMismatch;
    }
  };

  switchAccount = (accountId: string) => {
    const status = this.data.summary!.accountsSummary.find((account) => account.accountId === accountId)!.status;
    this.data = { summary: null, detail: null };
    if (status === AccountStatus.DRAFT) {
      history.push(ACCOUNTS_EDIT(accountId));
    } else if (status === AccountStatus.TO_BE_SIGNED) {
      history.push(ACCOUNTS_DOCUMENTS(accountId));
    } else {
      history.push(ACCOUNTS_DETAIL(accountId));
    }
  };

  // cancels all drafts or documents generation
  cancelChanges = async () => {
    const response = await this.apiStore.delete<SingleValueResponse<boolean>>(
      API_ACCOUNT_DETAIL_CHANGE(this.accountId),
    );
    return response?.data?.value;
  };

  // cancels request
  cancelRequest = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.apiStore.delete<SingleValueResponse<boolean>>(
      API_ACCOUNT_DETAIL_REQUEST(this.accountId),
    );
    if (response?.data?.value) {
      await this.fetchAccountDetail();
      this.pendingRequests = this.data.detail!.pendingRequestSignature;
    }
    this.hideConfirmRequest();
    this.rootStore.stores.uiStore.setLoading(false);
  };

  setNewEdit = (edit: boolean) => (this.newEdit = edit);

  edit = async (flow: Flows, page?: DetailPage) => {
    this.editFlow = flows[flow];
    if (this.confirmType === null) {
      await this.confirmEdit();
    } else {
      this.confirmDialog = true;
    }
  };

  confirmEdit = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    this.firstEdit = false;
    if (this.editFlow) {
      const response = await (this.editFlow.payload
        ? this.apiStore.post<SingleValueResponse<boolean>>(
            this.editFlow.endpoint(this.accountId),
            this.editFlow.payload,
          )
        : this.apiStore.patch<SingleValueResponse<boolean>>(this.editFlow.endpoint(this.accountId)));
      if (response?.status === 202 || response?.status === 200) {
        this.setFlow(this.editFlow);
        this.setNewEdit(true);
        this.setEditMode(this.editFlow.pending);
      }
    }

    this.rootStore.stores.uiStore.setLoading(false);
  };

  cancel = async () => {
    //noDetailReset?: boolean
    this.confirmDialog = true;
    //this.noDetailReset = noDetailReset;
  };

  confirmCancel = async () => {
    if (this.pendingChanges !== PendingChanges.NONE || this.processing || this.pendingSignature) {
      let cancelAllowed = true;

      if (this.pendingSignature) {
        this.rootStore.stores.uiStore.setLoading(true);
        const response = await this.apiStore.get<{ value: 'SIGNED' | 'PROCESS' | 'NONE' }>(
          API_ACCOUNT_DETAIL_CHECK_SIGNING(this.accountId),
        );
        this.rootStore.stores.uiStore.setLoading(false);
        // error
        if (!response?.data) {
          cancelAllowed = false;
          this.showInfoDialog('accountDetail_cancelChanges_error');
        }
        // already signed
        else if (response?.data?.value === 'SIGNED') {
          cancelAllowed = false;
          this.showInfoDialog('accountDetail_cancelChanges_signed');
        }
      }
      if (cancelAllowed) {
        this.rootStore.stores.uiStore.setLoading(true);
        const ok = await this.cancelChanges();
        this.rootStore.stores.uiStore.setLoading(false);
        if (ok) {
          this.setNewEdit(false);
          if (this.pendingSignature) {
            this.showInfoDialog('accountDetail_cancelChanges_done');
          }
          if (this.flow?.id === Flows.PLAN) {
            this.resetPlanCache();
          }
          /*
          if (this.noDetailReset) {
            this.unsetEditMode();
          } else {
            this.reset();
            await this.fetchData();
          }
          this.noDetailReset = undefined;
          */
          this.reset();
          await this.fetchData();
        }
      }
    }
  };

  setEditMode = (pending: PendingChanges) => {
    this.editMode = true;
    this.pendingChanges = pending;
  };

  unsetEditMode = () => {
    this.editMode = false;
    this.pendingChanges = PendingChanges.NONE;
  };

  // documents generation progressbar
  @action
  startProcessing = (typesParam?: string) => {
    if (typesParam) {
      this.types = typesParam;
    }
    this.processing = true;
    this.processingError = false;
    this.unsetEditMode(); // need to stay in current flow for the correct restart of docs generation
  };

  // stop/cancel documents generation progressbar, either manually or with an error from BE
  @action
  stopProcessing = (withError?: boolean) => {
    if (withError) {
      this.processingError = true;
    } else {
      this.flow = null;
      this.flowStepIndex = null;
      this.resetPlanCache();
      this.fetchData();
    }
  };

  resetPlanCache = () => {
    this.rootStore.stores.apiStore.removeFromCache(API_ACCOUNT_DETAIL_PLAN(this.accountId));
    this.rootStore.stores.apiStore.removeFromCache(API_ACCOUNT_DETAIL_PLAN_HISTORY(this.accountId));
  };

  @action
  refreshData = async () => {
    this.resetPlanCache();
    this.rootStore.stores.uiStore.setLoading(true);
    await this.rootStore.stores.apiStore.patch(API_ACCOUNT_DETAIL_EXT_PRODUCTS_REFRESH(this.accountId));
    this.rootStore.stores.uiStore.setLoading(false);
    this.refreshTimestamp = Date();
  };

  // default mode/view
  @action
  reset = () => {
    this.unsetEditMode();
    this.setPage(DetailPage.DASHBOARD);
    this.flow = null;
    this.flowStepIndex = null;
  };

  @computed
  get isEditDisabled(): boolean {
    return (
      this.locked ||
      !this.data.summary ||
      this.pendingSignature ||
      this.pendingWood ||
      this.pendingChanges !== PendingChanges.NONE ||
      this.processing
    );
  }

  // action allowed in status 03 - AWAITING_APPROVAL
  @computed
  get isEdit03Disabled(): boolean {
    return (
      this.locked ||
      !this.data.summary ||
      this.pendingSignature ||
      this.pendingChanges !== PendingChanges.NONE ||
      this.processing
    );
  }

  isEditTabDisabled = (tab?: DetailPage) => {
    if (!tab) return false;
    if (tab === DetailPage.PLAN && this.riskProfileMismatch) return true;
    return false;
  };

  @computed
  get isEditMode(): boolean {
    return this.editMode === true || this.pendingSignature || this.processingError;
  }

  @computed
  get confirmType(): null | ConfirmType {
    if (this.pendingSignature) {
      return ConfirmType.CANCEL_SIGNATURE;
    }
    if (this.processing) {
      return ConfirmType.CANCEL_PROCESSING;
    }
    if (!this.editMode && this.firstEdit) {
      return ConfirmType.EDIT;
    }
    if (this.editMode) {
      return ConfirmType.CANCEL_EDIT;
    }
    return null;
  }

  @action
  showInfoDialog = (text: string) => {
    this.infoDialog = true;
    this.infoText = text;
  };

  @action
  hideInfoDialog = () => {
    this.infoDialog = false;
    this.infoText = undefined;
  };

  @action
  hideConfirmDialog = () => {
    this.confirmDialog = false;
  };

  @action
  showConfirmRequest = () => {
    this.confirmRequest = true;
  };

  @action
  hideConfirmRequest = () => {
    this.confirmRequest = false;
  };

  resendAmendmentRequest = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.apiStore.patch(
      API_ACCOUNT_DETAIL_REQUEST_AMDMNT(this.accountId),
      undefined,
      true,
      true,
    );
    if (response?.status === 200) {
      await this.fetchBasicData();
      this.showInfoDialog(`client_resendAmendmentRequest_success`);
    } else {
      this.showInfoDialog(`client_resendAmendmentRequest_error`);
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  sendPortalActivation = async (clientAccess: ClientView, update?: boolean) => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = update
      ? await this.apiStore.patch<SingleValueResponse<boolean>>(
          API_ACCOUNT_DETAIL_SEND_PORTAL(this.accountId, clientAccess),
        )
      : await this.apiStore.post<SingleValueResponse<boolean>>(
          API_ACCOUNT_DETAIL_SEND_PORTAL(this.accountId, clientAccess),
        );
    this.rootStore.stores.uiStore.setLoading(false);
    if (response?.status === 200) {
      await this.fetchBasicData();
      this.showInfoDialog(`client_sendPortalActivation_success${update ? '_update' : ''}`);
    } else {
      this.showInfoDialog(`client_sendPortalActivation_error${update ? '_update' : ''}`);
    }
  };

  set2fa = async (use: boolean) => {
    this.rootStore.stores.uiStore.setLoading(true);
    if (
      this.pendingRequests.includes(Request.TWOFACTOR_DISABLE) ||
      this.pendingRequests.includes(Request.TWOFACTOR_ENABLE)
    ) {
      await this.apiStore.patch(API_ACCOUNT_DETAIL_REQUEST(this.accountId));
    } else {
      await this.apiStore.post<SingleValueResponse<boolean>>(API_ACCOUNT_DETAIL_SET_2FA(this.accountId), {
        enabled: use,
      });
    }
    this.fetchData();
    this.rootStore.stores.uiStore.setLoading(false);
  };

  set2faMethod = async (method: Method2FA) => {
    this.rootStore.stores.uiStore.setLoading(true);
    await this.apiStore.post<SingleValueResponse<boolean>>(API_ACCOUNT_DETAIL_SET_2FA_METHOD(this.accountId), {
      value: method,
    });
    this.fetchData();
    this.rootStore.stores.uiStore.setLoading(false);
  };

  terminateAccount = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.apiStore.post<SingleValueResponse<boolean>>(
      API_ACCOUNT_DETAIL_TERMINATE(this.accountId),
    );
    this.rootStore.stores.uiStore.setLoading(false);
    if (response?.status === 202) {
      await this.fetchData();
    } else if (response?.status === 400) {
      this.rootStore.stores.errorStore.addGlobalError('globalErrors_account.termination');
    }
  };
}
